import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, ArrowRight, CheckCircle2, Loader2, RotateCcw } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { useRetryExternalNoteActionMutation } from '@/services/api/externalNoteService';
import type {
  ExternalNoteActionSchema,
  ExternalNoteActionStatus,
} from '@/services/types/external-note-types';

interface CmsPushDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: ExternalNoteActionSchema;
}

const STATUS_DISPLAY: Record<
  ExternalNoteActionStatus,
  { label: string; className: string; icon: React.ElementType; animate?: boolean }
> = {
  PENDING: {
    label: 'Pending',
    className: 'bg-amber-50 text-amber-700 ring-amber-200/60',
    icon: Loader2,
    animate: true,
  },
  IN_PROGRESS: {
    label: 'In Progress',
    className: 'bg-violet-50 text-violet-700 ring-violet-200/60',
    icon: Loader2,
    animate: true,
  },
  COMPLETED: {
    label: 'Completed',
    className: 'bg-emerald-50 text-emerald-700 ring-emerald-200/60',
    icon: CheckCircle2,
  },
  FAILED: {
    label: 'Failed',
    className: 'bg-red-50 text-red-700 ring-red-200/60',
    icon: AlertCircle,
  },
};

function formatCmsType(cmsType: string): string {
  return cmsType
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatFieldValue(value: string | number | boolean | string[] | null): string {
  if (value == null || value === '') {
    return '\u2014';
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return String(value);
}

export const CmsPushDetailModal: React.FC<CmsPushDetailModalProps> = ({
  open,
  onOpenChange,
  action,
}) => {
  const statusConfig = STATUS_DISPLAY[action.status];
  const StatusIcon = statusConfig.icon;
  const [retryExternalNoteAction, { isLoading: isRetrying }] =
    useRetryExternalNoteActionMutation();

  const payload = action.payload as {
    content?: string;
    additional_fields?: Array<{
      external_key: string;
      field_type: string;
      value: string | number | boolean | string[] | null;
    }>;
  };

  const content = payload.content ?? '';
  const additionalFields = payload.additional_fields ?? [];
  const cmsLabel = formatCmsType(action.cms_type);
  const canRetry = action.status === 'FAILED';

  const handleRetry = async () => {
    try {
      await retryExternalNoteAction({
        matterId: action.matter_id,
        cmsType: action.cms_type,
        actionId: action.id,
      }).unwrap();

      toast.success(`Retrying push to ${cmsLabel}`);
      onOpenChange(false);
    } catch {
      toast.error(`Failed to retry push to ${cmsLabel}`);
    }
  };

  let timeLabel = `Created ${formatDistanceToNow(new Date(action.created_at), {
    addSuffix: true,
  })}`;

  if (action.completed_at) {
    timeLabel = `Completed ${formatDistanceToNow(new Date(action.completed_at), {
      addSuffix: true,
    })}`;
  } else if (action.started_at) {
    timeLabel = `Started ${formatDistanceToNow(new Date(action.started_at), {
      addSuffix: true,
    })}`;
  }

  return (
    <Dialog open={open} onClose={() => onOpenChange(false)} size="xl">
      <div className="flex items-center gap-2.5">
        <span className="flex h-7 w-7 items-center justify-center rounded-md border border-zinc-200 bg-zinc-50">
          <ArrowRight className="h-3.5 w-3.5 text-zinc-600" />
        </span>
        <DialogTitle>Pushed to {cmsLabel}</DialogTitle>
      </div>
      <DialogDescription>
        <span className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${statusConfig.className}`}
          >
            <StatusIcon
              className={`h-3 w-3 ${statusConfig.animate ? 'animate-spin' : ''}`}
            />
            {statusConfig.label}
          </span>
          <span className="text-zinc-400">{timeLabel}</span>
        </span>
      </DialogDescription>

      <DialogBody className="space-y-5">
        {/* Additional fields rendered read-only */}
        {additionalFields.length > 0 && (
          <div
            className={
              additionalFields.length >= 2
                ? 'grid grid-cols-2 gap-4'
                : 'space-y-4'
            }
          >
            {additionalFields.map((field) => (
              <div key={field.external_key} className="space-y-1">
                <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                  {field.external_key.replace(/_/g, ' ')}
                </span>
                <p className="text-sm text-zinc-900">
                  {formatFieldValue(field.value)}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Note content */}
        <div className="space-y-2">
          <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
            Note Content
          </span>
          <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 px-3 py-2.5 text-sm text-zinc-700 whitespace-pre-wrap">
            {content || '\u2014'}
          </div>
        </div>

        {/* Error message for failed actions */}
        {action.status === 'FAILED' && action.error_message && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
            <span className="text-xs font-medium uppercase tracking-wide text-red-600">
              Error
            </span>
            <p className="mt-1 text-sm text-red-700">{action.error_message}</p>
          </div>
        )}
      </DialogBody>

      <DialogActions>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Close
        </Button>
        {canRetry && (
          <Button onClick={handleRetry} disabled={isRetrying}>
            {isRetrying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Retrying...
              </>
            ) : (
              <>
                <RotateCcw className="mr-2 h-4 w-4" />
                Retry push
              </>
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
